import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import {
  DiagnosisRelevantVariablesReadDTO,
  DiagnosisRelevantVariablesCreateDTO,
} from '../../../generated/api-client';
import { apiClient } from '../../../services/apiClient';
import { ActionButton, UnitOfMeasurementDropdown } from '../../common';

interface EditRelevantVariableDialogProps {
  open: boolean;
  onClose: () => void;
  variable: DiagnosisRelevantVariablesReadDTO | null;
  diagnosisId: string;
  existingVariables?: DiagnosisRelevantVariablesReadDTO[]; // Per calcolare l'index
}

export const EditRelevantVariableDialog: React.FC<EditRelevantVariableDialogProps> = ({
  open,
  onClose,
  variable,
  diagnosisId,
  existingVariables = [],
}) => {
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty },
  } = useForm<DiagnosisRelevantVariablesCreateDTO>({
    defaultValues: variable
      ? {
          diagnosis_id: diagnosisId,
          table_code_index: String(variable.table_code_index ?? ''),
          variable: (variable as any).variable ?? undefined,
          unit_of_measurement: variable.unit_of_measurement ?? undefined,
          type: variable.type ?? undefined,
          hierarchy: variable.hierarchy ?? undefined,
          notes: variable.notes ?? undefined,
        }
      : undefined,
  });

  React.useEffect(() => {
    if (open) {
      if (variable) {
        reset({
          diagnosis_id: diagnosisId,
          table_code_index: String(variable.table_code_index ?? ''),
          variable: (variable as any).variable ?? undefined,
          unit_of_measurement: variable.unit_of_measurement ?? undefined,
          type: variable.type ?? undefined,
          hierarchy: variable.hierarchy ?? undefined,
          notes: variable.notes ?? undefined,
        });
      } else {
        // Reset per nuova variabile
        reset({
          diagnosis_id: diagnosisId,
          table_code_index: '',
          variable: undefined,
          unit_of_measurement: undefined,
          type: undefined,
          hierarchy: undefined,
          notes: undefined,
        });
      }
    }
  }, [variable, diagnosisId, reset, open]);

  const createMutation = useMutation(
    async (data: DiagnosisRelevantVariablesCreateDTO) => {
      return await apiClient.create_item_diagnosis_relevant_variables__post(data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(`diagnosis-relevant-variables-${diagnosisId}`);
        toast.success('Variabile pertinente creata con successo');
        reset();
        onClose();
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Errore nella creazione della variabile pertinente');
      },
    }
  );

  const updateMutation = useMutation(
    async (data: DiagnosisRelevantVariablesCreateDTO) => {
      if (!variable?.id) {
        throw new Error('ID variabile pertinente mancante');
      }
      return await apiClient.update_item_diagnosis_relevant_variables__item_id__put(
        variable.id,
        data
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(`diagnosis-relevant-variables-${diagnosisId}`);
        toast.success('Variabile pertinente aggiornata con successo');
        reset();
        onClose();
      },
      onError: (error: any) => {
        toast.error(error?.message || "Errore nell'aggiornamento della variabile pertinente");
      },
    }
  );

  const onSubmit = (data: DiagnosisRelevantVariablesCreateDTO) => {
    if (variable) {
      updateMutation.mutate(data);
    } else {
      // Calcola l'indice per la nuova variabile: ultimo indice + 1
      const maxIndex =
        existingVariables.length > 0
          ? Math.max(...existingVariables.map(v => (v as any).index ?? 0))
          : 0;
      const newIndex = maxIndex + 1;

      createMutation.mutate({
        ...data,
        index: newIndex,
      } as DiagnosisRelevantVariablesCreateDTO);
    }
  };

  const handleClose = () => {
    if (!updateMutation.isLoading && !createMutation.isLoading) {
      reset();
      onClose();
    }
  };

  const isSubmitting = updateMutation.isLoading || createMutation.isLoading;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{variable ? 'Modifica variabile pertinente' : 'Nuova variabile pertinente'}</span>
          <ActionButton
            icon={<CloseIcon />}
            tooltip="Chiudi"
            onClick={handleClose}
            variant="outlined"
          />
        </Box>
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Variabile"
                {...register('variable')}
                error={!!errors.variable}
                helperText={errors.variable?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="ID contatore cliente"
                {...register('table_code_index', { required: 'Campo obbligatorio' })}
                error={!!errors.table_code_index}
                helperText={errors.table_code_index?.message}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="unit_of_measurement"
                control={control}
                render={({ field, fieldState }) => (
                  <UnitOfMeasurementDropdown
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Tipologia"
                {...register('type')}
                error={!!errors.type}
                helperText={errors.type?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Gerarchia"
                {...register('hierarchy', { valueAsNumber: true })}
                error={!!errors.hierarchy}
                helperText={errors.hierarchy?.message}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Note"
                {...register('notes')}
                error={!!errors.notes}
                helperText={errors.notes?.message}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Stack direction="row" spacing={2} justifyContent="center" sx={{ width: '100%', py: 1 }}>
            <ActionButton
              icon={<SaveIcon />}
              tooltip="Salva"
              onClick={handleSubmit(onSubmit)}
              variant="filled"
              disabled={isSubmitting || !isDirty}
              loading={isSubmitting}
            />
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
};
